import { useRef } from 'react';
import './DateTextPicker.css';

export function isIsoDateString(s) {
  if (s == null || typeof s !== 'string') return false;
  const t = s.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return false;
  const [y, m, d] = t.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

function normalizeDateBlur(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  if (isIsoDateString(t)) return t;
  const norm = t.replace(/\//g, '-');
  const m = norm.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = m[1];
    const mo = m[2].padStart(2, '0');
    const day = m[3].padStart(2, '0');
    const cand = `${y}-${mo}-${day}`;
    if (isIsoDateString(cand)) return cand;
  }
  return t;
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 2v3M16 2v3M4 9h16M5 5h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * ISO date (YYYY-MM-DD) as text (typeable) plus a button that opens the native date picker.
 */
export default function DateTextPicker({ id, label, value, onChange, disabled = false }) {
  const nativeRef = useRef(null);

  const openCalendar = () => {
    const el = nativeRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
      } else {
        el.focus();
        el.click?.();
      }
    } catch {
      el.focus();
    }
  };

  const handleBlur = () => {
    const n = normalizeDateBlur(value);
    if (isIsoDateString(n) && n !== value) onChange(n);
  };

  return (
    <div className="form-group date-text-picker">
      {label ? <label htmlFor={id}>{label}</label> : null}
      <div className="date-text-picker-row">
        <input
          id={id}
          type="text"
          className="date-text-picker-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="YYYY-MM-DD"
          maxLength={10}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
        />
        <button
          type="button"
          className="date-text-picker-cal"
          onClick={openCalendar}
          disabled={disabled}
          aria-label={label ? `Open calendar: ${label}` : 'Open calendar'}
          title="Pick date"
        >
          <CalendarIcon />
        </button>
        <input
          ref={nativeRef}
          type="date"
          className="date-text-picker-native"
          value={isIsoDateString(value) ? value.trim() : ''}
          onChange={e => {
            const v = e.target.value;
            if (v) onChange(v);
          }}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
